import React, { useState, useCallback } from "react";
import { CheckCircle, DollarSign, Target, ExternalLink, ShoppingCart, Zap, Brain, Loader, Award, Star, Trophy, ChevronRight, ChevronLeft, Users } from "lucide-react";
import SEO, { PAGE_SEO } from "./SEO";
import { UserProfile, GolfClub, ClubType } from "../types";
import { aiService } from "../services/aiService";
import { stripMarkdown } from "../utils/formatAIText";
import { STARTER_SETS, calculateMatchScore } from "../data/starterSets";
import { CLUB_DATA } from "../utils/calculations";

interface BeginnerBuilderProps {
  userProfile: UserProfile | null;
  onProfileUpdate: (profile: UserProfile) => Promise<boolean> | void;
}

interface StarterSet {
  id: string;
  name: string;
  description: string;
  tier: "good" | "better" | "best";
  priceRange: string;
  clubs: {
    type: ClubType;
    brand: string;
    model: string;
    loft?: number; // Optional - some clubs are part of sets
    description: string;
    whyIncluded?: string; // Why this club is included
  }[];
  benefits: string[];
  bestFor: string[];
  whyRecommended?: string;
  ballRecommendation?: {
    category: "distance" | "balanced" | "tour";
    model: string;
    reason: string;
  };
  affiliateLinks?: {
    amazon?: string;
    globalGolf?: string;
    pga?: string;
    rogerDunn?: string;
  };
  estimatedTotal?: number;
}

const BeginnerBuilder: React.FC<BeginnerBuilderProps> = ({ userProfile, onProfileUpdate }) => {
  // Wizard state
  const [currentStep, setCurrentStep] = useState(0);
  const [showResults, setShowResults] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Question answers
  const [selectedFrequency, setSelectedFrequency] = useState<string>(
    userProfile?.courseFrequency || "monthly"
  );
  const [selectedCourseType, setSelectedCourseType] = useState<string>(
    userProfile?.preferredCourseType || "municipal"
  );
  const [selectedMissPattern, setSelectedMissPattern] = useState<string>(
    userProfile?.missPattern || "inconsistent"
  );
  const [selectedConfidenceClub] = useState<string>("7-iron");
  const [selectedBudget, setSelectedBudget] = useState<string>("500-1200");
  const [selectedSkillLevel, setSelectedSkillLevel] = useState<string>(
    userProfile?.skillLevel || "beginner"
  );
  
  // Results
  const [starterSets, setStarterSets] = useState<StarterSet[]>([]);
  const [recommendedSetId, setRecommendedSetId] = useState<string | null>(null);

  // Wizard steps configuration
  const wizardSteps = [
    {
      id: 'budget',
      title: 'What is your budget for a starter set?',
      subtitle: 'Be honest - we have great options at every price point!',
      icon: DollarSign,
      options: [
        { value: 'under-500', label: '💰 Under $500', description: 'Best value sets to get started' },
        { value: '500-1200', label: '💎 $500 - $1,200', description: 'Quality clubs that will last years' },
        { value: 'over-1200', label: '👑 $1,200+', description: 'Premium equipment investment' },
      ],
    },
    {
      id: 'frequency',
      title: 'How often do you plan to play?',
      subtitle: 'This helps us match equipment to your commitment level',
      icon: Target,
      options: [
        { value: 'rarely', label: '⛳ A few times a year', description: 'Special occasions, casual' },
        { value: 'monthly', label: '📅 Once a month', description: 'Regular recreational play' },
        { value: 'weekly', label: '🏌️ Weekly', description: 'Golf is a hobby' },
        { value: 'multiple', label: '🔥 Multiple times/week', description: 'Very committed player' },
      ],
    },
    {
      id: 'skillLevel',
      title: 'How would you describe your current skill level?',
      subtitle: 'Be honest - this ensures the right equipment difficulty',
      icon: Trophy,
      options: [
        { value: 'beginner', label: '🌱 Beginner', description: 'Just starting, learning basics' },
        { value: 'intermediate', label: '📈 Intermediate', description: 'Played some, working to improve' },
        { value: 'advanced', label: '⭐ Advanced', description: 'Experienced, consistent player' },
      ],
    },
    {
      id: 'missPattern',
      title: 'What is your most common miss?',
      subtitle: 'We will recommend equipment to help fix this!',
      icon: Target,
      options: [
        { value: 'slice', label: '➡️ Slice (big curve right)', description: 'Ball curves way right' },
        { value: 'hook', label: '⬅️ Hook (big curve left)', description: 'Ball curves way left' },
        { value: 'inconsistent', label: '🎲 Inconsistent', description: 'Different every time' },
        { value: 'thin', label: '⬇️ Thin shots', description: 'Topping or hitting thin' },
        { value: 'fat', label: '⬆️ Fat shots', description: 'Hitting behind the ball' },
      ],
    },
    {
      id: 'courseType',
      title: 'What type of courses will you play most?',
      subtitle: 'Different courses benefit from different equipment',
      icon: Users,
      options: [
        { value: 'municipal', label: '🏛️ Municipal', description: 'Public, affordable courses' },
        { value: 'parkland', label: '🌳 Parkland', description: 'Tree-lined, traditional' },
        { value: 'resort', label: '🏖️ Resort', description: 'Vacation golf' },
        { value: 'links', label: '🌊 Links', description: 'Open, windy, seaside' },
      ],
    },
  ];

  const isLastStep = currentStep === wizardSteps.length - 1;
  const currentStepData = wizardSteps[currentStep];

  const handleAnswer = (questionId: string, value: string) => {
    switch(questionId) {
      case 'budget':
        setSelectedBudget(value);
        break;
      case 'frequency':
        setSelectedFrequency(value);
        break;
      case 'skillLevel':
        setSelectedSkillLevel(value);
        break;
      case 'missPattern':
        setSelectedMissPattern(value);
        break;
      case 'courseType':
        setSelectedCourseType(value);
        break;
    }
  };

  const nextStep = () => {
    if (isLastStep) {
      runAIAnalysis();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const getCurrentAnswer = (questionId: string): string => {
    switch(questionId) {
      case 'budget': return selectedBudget;
      case 'frequency': return selectedFrequency;
      case 'skillLevel': return selectedSkillLevel;
      case 'missPattern': return selectedMissPattern;
      case 'courseType': return selectedCourseType;
      default: return '';
    }
  };

  const calculateRecommendation = useCallback(() => {
    let recommendedId = "better";
    
    // Primary factor: Budget
    if (selectedBudget === "under-500") {
      recommendedId = "good";
    } else if (selectedBudget === "over-1200") {
      recommendedId = "best";
    } else {
      // Mid-budget: Consider other factors
      if (selectedFrequency === "rarely" || selectedSkillLevel === "beginner") {
        recommendedId = "good";
      } else if (selectedFrequency === "multiple" || selectedSkillLevel === "intermediate" || selectedSkillLevel === "advanced") {
        recommendedId = "best";
      } else {
        recommendedId = "better";
      }
      
      // Course type can upgrade recommendation
      if (selectedCourseType === "links" && selectedFrequency !== "rarely") {
        recommendedId = "best";
      }
    }
    
    setRecommendedSetId(recommendedId);
  }, [selectedBudget, selectedFrequency, selectedSkillLevel, selectedCourseType]);

  const generateStarterSets = useCallback(() => {
    // Use new comprehensive starter sets system
    const budgetTier = selectedBudget === "under-500" ? 'budget' :
                       selectedBudget === "over-1200" ? 'premium' : 'mid-range';
    
    const tierSets = STARTER_SETS.find(t => t.tier === budgetTier);
    if (!tierSets) return;
    
    // Calculate match scores based on user profile
    const setsWithScores = tierSets.options.map(option => {
      const score = calculateMatchScore(option, {
        handicap: userProfile?.handicap || 18,
        budget: selectedBudget === "under-500" ? 450 : selectedBudget === "over-1200" ? 2000 : 1000,
        skillLevel: selectedSkillLevel,
        courseFrequency: selectedFrequency
      });
      
      return {
        ...option,
        matchScore: score
      };
    });
    
    // Sort by match score
    setsWithScores.sort((a, b) => b.matchScore - a.matchScore);
    
    // Map to component's StarterSet interface
    const mappedSets: StarterSet[] = setsWithScores.map(set => ({
      id: set.id,
      name: set.name,
      description: set.description,
      tier: (set.matchLevel === 'perfect' ? 'best' : set.matchLevel === 'great' ? 'better' : 'good') as 'good' | 'better' | 'best',
      priceRange: `$${set.estimatedTotal}`,
      clubs: set.clubs,
      benefits: set.keyAdvantages,
      bestFor: set.idealFor,
      whyRecommended: `${set.matchScore}% Match - ${set.description}`,
      ballRecommendation: {
        category: "balanced" as const,
        model: set.ballRecommendation.model,
        reason: set.ballRecommendation.reason
      }
    }));
    
    setStarterSets(mappedSets);
    setRecommendedSetId(mappedSets[0]?.id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBudget, selectedFrequency, selectedSkillLevel, userProfile]);

  // OLD generateStarterSets REMOVED - using new comprehensive system above
  
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const oldGenerateStarterSets = useCallback(() => {
    // LEGACY CODE REMOVED - now using comprehensive starterSets.ts system
    // Keeping function signature for reference
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const legacyAllSets: StarterSet[] = [
      {
        id: "good",
        name: "Essential Starter Set",
        description: "Everything you need to get started with confidence",
        tier: "good",
        priceRange: selectedBudget === "under-500" ? "$350 - $500" : "$400 - $600",
        clubs: selectedMissPattern === "slice" ? [
          { type: "driver", brand: "Ping", model: "G430 MAX", loft: 12, description: "Maximum forgiveness for slice correction" },
          { type: "5-wood", brand: "Ping", model: "G430", loft: 18, description: "Easy launch fairway wood" },
          { type: "5-hybrid", brand: "Ping", model: "G430", loft: 25, description: "Forgiving hybrid" },
          { type: "6-iron", brand: "Ping", model: "G430", loft: 28, description: "Game improvement iron" },
          { type: "7-iron", brand: "Ping", model: "G430", loft: 32, description: "Most versatile iron" },
          { type: "8-iron", brand: "Ping", model: "G430", loft: 36, description: "Short approach shots" },
          { type: "9-iron", brand: "Ping", model: "G430", loft: 41, description: "High lofted iron" },
          { type: "pw", brand: "Ping", model: "G430", loft: 46, description: "Pitching wedge" },
          { type: "sw", brand: "Cleveland", model: "CBX Full-Face", loft: 56, description: "Forgiving sand wedge" },
          { type: "putter", brand: "Odyssey", model: "White Hot OG", loft: 3, description: "Alignment-friendly putter" }
        ] : [
          { type: "driver", brand: "Wilson", model: "Launch Pad", loft: 12, description: "Forgiving driver with high launch" },
          { type: "5-wood", brand: "TaylorMade", model: "Stealth 2", loft: 18, description: "Easy to hit fairway wood" },
          { type: "5-hybrid", brand: "Cobra", model: "F-MAX", loft: 25, description: "Replaces long irons" },
          { type: "6-iron", brand: "Wilson", model: "Launch Pad", loft: 28, description: "Game improvement iron" },
          { type: "7-iron", brand: "Wilson", model: "Launch Pad", loft: 32, description: "Most versatile iron" },
          { type: "8-iron", brand: "Wilson", model: "Launch Pad", loft: 36, description: "Short approach shots" },
          { type: "9-iron", brand: "Wilson", model: "Launch Pad", loft: 41, description: "High lofted iron" },
          { type: "pw", brand: "Wilson", model: "Launch Pad", loft: 46, description: "Pitching wedge" },
          { type: "sw", brand: "Titleist", model: "Vokey SM9", loft: 56, description: "Tour-proven sand wedge" },
          { type: "putter", brand: "Scotty Cameron", model: "Special Select", loft: 3, description: "Premium putter" }
        ],
        benefits: [
          "Complete set to get started",
          "Forgiving and easy to hit",
          "Great value for money",
          "Includes carry bag"
        ],
        bestFor: [
          "Complete beginners",
          "Occasional players",
          "Budget-conscious golfers"
        ],
        ballRecommendation: {
          category: "distance",
          model: "Top Flite XL Distance",
          reason: "Durable, low-spin ball perfect for beginners focusing on distance over feel"
        },
        affiliateLinks: {
          amazon: "https://www.amazon.com/Wilson-Launch-Starter-Complete-Right/dp/B07QKQZQM9",
          globalGolf: "https://www.globalgolf.com/golf-clubs/complete-sets/wilson-launch-pad/",
          pga: "https://www.pgatoursuperstore.com/wilson-launch-pad-complete-set",
          rogerDunn: "https://www.rogerdunn.com/wilson-launch-pad-complete-set"
        },
        estimatedTotal: 549
      },
      {
        id: "better",
        name: "Progressive Improvement Set",
        description: "Quality clubs that grow with your game",
        tier: "better",
        priceRange: "$800 - $1,200",
        clubs: selectedCourseType === "links" ? [
          { type: "driver", brand: "TaylorMade", model: "Stealth 2", loft: 10.5, description: "Low-spin driver for windy conditions" },
          { type: "3-wood", brand: "TaylorMade", model: "Stealth 2", loft: 15, description: "Penetrating ball flight" },
          { type: "5-hybrid", brand: "Ping", model: "G430", loft: 25, description: "Consistent in wind" },
          { type: "6-iron", brand: "Mizuno", model: "JPX923 Hot Metal", loft: 28, description: "Distance and feel" },
          { type: "7-iron", brand: "Mizuno", model: "JPX923 Hot Metal", loft: 32, description: "Precise iron play" },
          { type: "8-iron", brand: "Mizuno", model: "JPX923 Hot Metal", loft: 36, description: "Approach shots" },
          { type: "9-iron", brand: "Mizuno", model: "JPX923 Hot Metal", loft: 41, description: "Short iron control" },
          { type: "pw", brand: "Mizuno", model: "JPX923 Hot Metal", loft: 46, description: "Pitching wedge" },
          { type: "gw", brand: "Titleist", model: "Vokey SM9", loft: 50, description: "Gap wedge versatility" },
          { type: "sw", brand: "Titleist", model: "Vokey SM9", loft: 56, description: "Tour-level sand wedge" },
          { type: "putter", brand: "Ping", model: "Anser 2", loft: 3, description: "Classic blade putter" }
        ] : [
          { type: "driver", brand: "Cobra", model: "F-MAX Superlite", loft: 10.5, description: "Lightweight, forgiving driver" },
          { type: "3-wood", brand: "Callaway", model: "Rogue ST MAX", loft: 15, description: "AI-designed fairway wood" },
          { type: "5-hybrid", brand: "Cobra", model: "F-MAX", loft: 25, description: "Easy launch hybrid" },
          { type: "6-iron", brand: "Cobra", model: "F-MAX One", loft: 28, description: "Super game improvement" },
          { type: "7-iron", brand: "Cobra", model: "F-MAX One", loft: 32, description: "Maximum forgiveness" },
          { type: "8-iron", brand: "Cobra", model: "F-MAX One", loft: 36, description: "High launch iron" },
          { type: "9-iron", brand: "Cobra", model: "F-MAX One", loft: 41, description: "Short iron precision" },
          { type: "pw", brand: "Cobra", model: "F-MAX One", loft: 46, description: "Pitching wedge" },
          { type: "gw", brand: "Cleveland", model: "RTX 6 ZipCore", loft: 50, description: "Enhanced forgiveness gap wedge" },
          { type: "sw", brand: "Cleveland", model: "RTX 6 ZipCore", loft: 56, description: "Tour-level sand wedge" },
          { type: "putter", brand: "Odyssey", model: "Stroke Lab", loft: 3, description: "Multi-material putter" }
        ],
        benefits: [
          "Higher quality construction",
          "Better feel and feedback",
          "More club options",
          "Stand bag included",
          "Room to grow skills"
        ],
        bestFor: [
          "Committed beginners",
          "Monthly players",
          "Those wanting quality"
        ],
        ballRecommendation: {
          category: "balanced",
          model: "Bridgestone e6 Soft",
          reason: "Mid-spin ball that balances distance and feel, perfect for improving players"
        },
        affiliateLinks: {
          amazon: "https://www.amazon.com/Cobra-F-MAX-Complete-Set/dp/B08XYQZXYZ",
          globalGolf: "https://www.globalgolf.com/golf-clubs/complete-sets/cobra-f-max/",
          pga: "https://www.pgatoursuperstore.com/cobra-f-max-complete-set",
          rogerDunn: "https://www.rogerdunn.com/cobra-f-max-complete-set"
        },
        estimatedTotal: 999
      },
      {
        id: "best",
        name: "Premium Starter Collection",
        description: "Top-tier equipment for serious beginners",
        tier: "best",
        priceRange: "$1,500 - $2,200",
        clubs: [
          { type: "driver", brand: "Callaway", model: "Big Bertha B21", loft: 10.5, description: "AI-designed for forgiveness" },
          { type: "3-wood", brand: "Callaway", model: "Big Bertha B21", loft: 15, description: "Easy launch fairway" },
          { type: "5-wood", brand: "Callaway", model: "Big Bertha B21", loft: 18, description: "High launch wood" },
          { type: "4-hybrid", brand: "Callaway", model: "Big Bertha B21", loft: 22, description: "Long iron replacement" },
          { type: "5-hybrid", brand: "Callaway", model: "Big Bertha B21", loft: 25, description: "Versatile hybrid" },
          { type: "6-iron", brand: "Callaway", model: "Big Bertha B21", loft: 28, description: "Premium game improvement" },
          { type: "7-iron", brand: "Callaway", model: "Big Bertha B21", loft: 32, description: "Confidence inspiring" },
          { type: "8-iron", brand: "Callaway", model: "Big Bertha B21", loft: 36, description: "High launch iron" },
          { type: "9-iron", brand: "Callaway", model: "Big Bertha B21", loft: 41, description: "Precise short iron" },
          { type: "pw", brand: "Callaway", model: "Big Bertha B21", loft: 46, description: "Pitching wedge" },
          { type: "gw", brand: "Callaway", model: "Jaws MD5", loft: 52, description: "Premium gap wedge" },
          { type: "sw", brand: "Callaway", model: "Jaws MD5", loft: 56, description: "Tour-level sand wedge" },
          { type: "putter", brand: "Odyssey", model: "White Hot OG", loft: 3, description: "Premium insert putter" }
        ],
        benefits: [
          "Premium materials and tech",
          "Maximum forgiveness",
          "Complete 13-club set",
          "Premium cart bag",
          "Professional fitting included",
          "2-year warranty"
        ],
        bestFor: [
          "Serious beginners",
          "Weekly players",
          "Those wanting the best",
          "Investment in long-term improvement"
        ],
        ballRecommendation: {
          category: "tour",
          model: "Titleist Pro V1",
          reason: "Premium urethane ball for maximum feel and control as skills develop"
        },
        affiliateLinks: {
          amazon: "https://www.amazon.com/Callaway-Big-Bertha-Complete-Set/dp/B09ABCDEFG",
          globalGolf: "https://www.globalgolf.com/golf-clubs/complete-sets/callaway-big-bertha/",
          pga: "https://www.pgatoursuperstore.com/callaway-big-bertha-complete-set",
          rogerDunn: "https://www.rogerdunn.com/callaway-big-bertha-complete-set"
        },
        estimatedTotal: 1849
      }
    ];
    // LEGACY CODE - not used anymore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runAIAnalysis = useCallback(async () => {
    setIsAnalyzing(true);
    
    try {
      // First generate the sets
      generateStarterSets();
      calculateRecommendation();
      
      // Then get AI-powered recommendations for each set
      const sets = starterSets.length > 0 ? starterSets : [];
      
      if (sets.length > 0) {
        const aiPromises = sets.map(async (set) => {
          try {
            const aiExplanation = await aiService.getAIStarterSetRecommendations({
              frequency: selectedFrequency,
              courseType: selectedCourseType,
              missPattern: selectedMissPattern,
              confidenceClub: selectedConfidenceClub,
              budget: selectedBudget,
              skillLevel: selectedSkillLevel,
              tier: set.tier
            });
            
            return { ...set, whyRecommended: aiExplanation };
          } catch (error) {
            console.error('AI recommendation error:', error);
            // Return set with existing explanation if AI fails
            return set;
          }
        });
        
        const enhancedSets = await Promise.all(aiPromises);
        setStarterSets(enhancedSets);
      }
    } catch (error) {
      console.error('Error during AI analysis:', error);
    }
    
    setIsAnalyzing(false);
    setShowResults(true);
  }, [generateStarterSets, calculateRecommendation, starterSets, selectedFrequency, selectedCourseType, selectedMissPattern, selectedConfidenceClub, selectedBudget, selectedSkillLevel]);

  const selectStarterSet = (setId: string) => {
    const selectedSet = starterSets.find(set => set.id === setId);
    if (!selectedSet) return;

    if (!userProfile) {
      alert("Great choice! Please complete your profile first to save this starter set to your golf bag.");
      return;
    }

    const newGolfBag: GolfClub[] = selectedSet.clubs.map((club, index) => ({
      id: `starter-${setId}-${index}`,
      type: club.type,
      brand: club.brand,
      model: club.model,
      shaftType: club.type === "driver" || club.type.includes("wood") || club.type.includes("hybrid") ? "graphite" : "steel",
      shaftFlex: "regular",
      lie: 60,
      loft: club.loft || CLUB_DATA[club.type]?.loft || 45, // Use club loft or default from CLUB_DATA
      length: 36,
      isInBag: true,
      distance: undefined,
      isDistanceEstimated: true,
      notes: `From ${selectedSet.name} starter set`
    }));

    const updatedProfile: UserProfile = {
      ...userProfile,
      golfBag: newGolfBag,
      courseFrequency: selectedFrequency as any,
      preferredCourseType: selectedCourseType as any,
      updatedAt: new Date()
    };

    onProfileUpdate(updatedProfile);
    
    // Redirect to profile page instead of showing alert
    setTimeout(() => {
      window.location.href = '/profile';
    }, 500);
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case "good": return "border-green-200 bg-green-50";
      case "better": return "border-blue-200 bg-blue-50";
      case "best": return "border-purple-200 bg-purple-50";
      default: return "border-gray-200 bg-gray-50";
    }
  };

  const getTierTextColor = (tier: string) => {
    switch (tier) {
      case "good": return "text-green-800";
      case "better": return "text-blue-800";
      case "best": return "text-purple-800";
      default: return "text-gray-800";
    }
  };

  return (
    <>
      <SEO {...PAGE_SEO.beginnerBuilder} url="/beginner-builder" />
    <div className="max-w-7xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {showResults ? "Your AI-Powered Golf Recommendations" : "AI Golf Club Fitting"}
        </h1>
        <p className="text-gray-600">
          {showResults 
            ? "Personalized equipment recommendations based on your playing style" 
            : "Answer a few questions and let AI find the perfect clubs for your game"
          }
        </p>
        {!userProfile && !showResults && (
          <div className="mt-4 p-4 bg-pink-50 rounded-lg">
            <p className="text-pink-700 text-sm">
              💡 <strong>Tip:</strong> Complete your profile to get even more personalized recommendations!
            </p>
          </div>
        )}
      </div>

      {/* Wizard or Results */}
      {!showResults ? (
        <div className="max-w-3xl mx-auto">
          {/* Progress Bar */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm font-medium text-gray-700">
                Question {currentStep + 1} of {wizardSteps.length}
              </span>
              <span className="text-sm text-gray-500">
                {Math.round(((currentStep + 1) / wizardSteps.length) * 100)}% Complete
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full transition-all duration-500"
                style={{ width: `${((currentStep + 1) / wizardSteps.length) * 100}%` }}
              />
            </div>
          </div>

          {/* Current Question */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            {/* Question Icon */}
            <div className="flex justify-center mb-6">
              <div className="bg-gradient-to-r from-blue-500 to-purple-500 p-4 rounded-2xl">
                <currentStepData.icon className="w-8 h-8 text-white" />
              </div>
            </div>

            {/* Question Text */}
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 text-center mb-3">
              {currentStepData.title}
            </h2>
            <p className="text-gray-600 text-center mb-8">
              {currentStepData.subtitle}
            </p>

            {/* Answer Options */}
            <div className="space-y-3 mb-8">
              {currentStepData.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    handleAnswer(currentStepData.id, option.value);
                    // Auto-advance after selection (small delay for visual feedback)
                    setTimeout(() => nextStep(), 300);
                  }}
                  className={`w-full text-left p-6 rounded-xl border-2 transition-all duration-200 ${
                    getCurrentAnswer(currentStepData.id) === option.value
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 bg-white hover:border-blue-300 hover:shadow-sm'
                  }`}
                >
                  <div className="flex items-start space-x-4">
                    <div className={`flex-shrink-0 w-6 h-6 rounded-full border-2 mt-1 ${
                      getCurrentAnswer(currentStepData.id) === option.value
                        ? 'border-blue-500 bg-blue-500'
                        : 'border-gray-300'
                    }`}>
                      {getCurrentAnswer(currentStepData.id) === option.value && (
                        <CheckCircle className="w-5 h-5 text-white -ml-[1px] -mt-[1px]" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="text-lg font-semibold text-gray-900 mb-1">
                        {option.label}
                      </div>
                      <div className="text-sm text-gray-600">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between items-center pt-6 border-t border-gray-200">
              <button
                onClick={prevStep}
                disabled={currentStep === 0}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-semibold transition-colors ${
                  currentStep === 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-gray-700 hover:bg-gray-100'
                }`}
              >
                <ChevronLeft className="w-5 h-5" />
                <span>Back</span>
              </button>

              <button
                onClick={nextStep}
                disabled={!getCurrentAnswer(currentStepData.id) || isAnalyzing}
                className={`flex items-center space-x-2 px-8 py-3 rounded-lg font-semibold transition-all ${
                  !getCurrentAnswer(currentStepData.id) || isAnalyzing
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-gradient-to-r from-blue-600 to-purple-600 text-white hover:from-blue-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
                }`}
              >
                {isAnalyzing ? (
                  <>
                    <Loader className="w-5 h-5 animate-spin" />
                    <span>Analyzing...</span>
                  </>
                ) : isLastStep ? (
                  <>
                    <span>Get Recommendations</span>
                    <Zap className="w-5 h-5" />
                  </>
                ) : (
                  <>
                    <span>Next</span>
                    <ChevronRight className="w-5 h-5" />
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* AI Results Section */
        <div className="space-y-6">
          {/* Results Header */}
          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-2xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                  <Brain className="w-6 h-6 mr-2 text-blue-600" />
                  AI Analysis Complete
                </h3>
                <p className="text-gray-600">
                  Found {starterSets.length} personalized starter {starterSets.length === 1 ? 'set' : 'sets'} based on your profile
                </p>
              </div>
              <button
                onClick={() => setShowResults(false)}
                className="px-4 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors"
              >
                Back to Questions
              </button>
            </div>
          </div>

          {/* AI-Enhanced Starter Sets Grid */}
          <div className="grid lg:grid-cols-3 gap-8">
            {starterSets.map((set, index) => {
              // Determine match level based on position
              const matchLevel = index === 0 ? 'perfect' : index === 1 ? 'great' : 'good';
              const matchIcon = matchLevel === 'perfect' ? Trophy : matchLevel === 'great' ? Star : Award;
              const MatchIcon = matchIcon;
              
              return (
              <div key={set.id} className={`rounded-2xl border-2 ${getTierColor(set.tier)} ${set.id === recommendedSetId ? 'ring-4 ring-blue-300 ring-opacity-50 shadow-xl' : ''} p-6 relative transition-all duration-300 flex flex-col h-full`}>
                {/* Match Level Badge */}
                <div className="absolute -top-3 left-6 flex gap-2">
                  <span className={`px-4 py-1 rounded-full text-sm font-bold flex items-center gap-1 ${
                    matchLevel === 'perfect' ? 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-yellow-900 border-2 border-yellow-600' :
                    matchLevel === 'great' ? 'bg-gradient-to-r from-blue-400 to-blue-500 text-blue-900 border-2 border-blue-600' :
                    'bg-gradient-to-r from-green-400 to-green-500 text-green-900 border-2 border-green-600'
                  }`}>
                    <MatchIcon size={14} />
                    {matchLevel.toUpperCase()} MATCH
                  </span>
                  <span className={`px-3 py-1 rounded-full text-sm font-bold ${getTierTextColor(set.tier)} bg-white border-2 ${set.tier === 'good' ? 'border-green-200' : set.tier === 'better' ? 'border-blue-200' : 'border-purple-200'}`}>
                    {set.tier.toUpperCase()}
                  </span>
                </div>

                {/* AI Recommended Badge */}
                {set.id === recommendedSetId && (
                  <div className="absolute -top-3 right-6">
                    <span className="px-3 py-1 bg-gradient-to-r from-blue-500 to-purple-500 text-white text-xs font-bold rounded-full flex items-center">
                      <Brain className="w-3 h-3 mr-1" />
                      AI RECOMMENDED
                    </span>
                  </div>
                )}

                {/* Main Content - Flexible */}
                <div className="flex-1">
                  {/* Header */}
                  <div className="mt-4 mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{set.name}</h3>
                    <p className="text-gray-600 mb-4">{set.description}</p>
                    <div className="flex items-center space-x-2 mb-4">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <span className="text-lg font-bold text-green-600">{set.priceRange}</span>
                    </div>
                  </div>

                  {/* Clubs */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Included Clubs ({set.clubs.length})</h4>
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {set.clubs.map((club, index) => (
                        <div key={index} className="flex items-center justify-between text-sm">
                          <span className="font-medium">{club.type.toUpperCase()}</span>
                          <span className="text-gray-600">{club.brand} {club.model}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Benefits */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Benefits</h4>
                    <div className="space-y-2">
                      {set.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                          <span className="text-gray-700">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Best For */}
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Best For</h4>
                    <div className="space-y-2">
                      {set.bestFor.map((item, index) => (
                        <div key={index} className="flex items-center space-x-2 text-sm">
                          <Target className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-gray-700">{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* AI Why Recommended */}
                  {set.whyRecommended && (
                    <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg border border-blue-200">
                      <h4 className="font-semibold text-blue-900 mb-2 flex items-center">
                        <Brain className="w-4 h-4 mr-2" />
                        AI Analysis: Why This Fits You
                      </h4>
                      <p className="text-sm text-blue-800">{stripMarkdown(set.whyRecommended || '')}</p>
                    </div>
                  )}

                  {/* Ball Recommendation */}
                  {set.ballRecommendation && (
                    <div className="mb-6 p-3 bg-green-50 rounded-lg border border-green-200">
                      <h4 className="font-semibold text-green-900 mb-2 text-sm">Recommended Golf Ball</h4>
                      <div className="text-sm">
                        <div className="font-medium text-green-800">{set.ballRecommendation.model}</div>
                        <div className="text-green-700 mt-1">{set.ballRecommendation.reason}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Bottom Section - Fixed Position */}
                <div className="mt-auto space-y-4">
                  {/* Shopping Options */}
                  {set.affiliateLinks && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold text-gray-900 flex items-center">
                          <ShoppingCart className="w-4 h-4 mr-2" />
                          Shop This Set
                        </h4>
                        {set.estimatedTotal && (
                          <span className="text-lg font-bold text-green-600">
                            ~${set.estimatedTotal.toLocaleString()}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {set.affiliateLinks.amazon && (
                          <a
                            href={set.affiliateLinks.amazon}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-3 py-2 bg-orange-500 hover:bg-orange-600 text-white text-sm rounded-lg transition-colors font-medium"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Amazon
                          </a>
                        )}
                        {set.affiliateLinks.globalGolf && (
                          <a
                            href={set.affiliateLinks.globalGolf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors font-medium"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            GlobalGolf
                          </a>
                        )}
                        {set.affiliateLinks.pga && (
                          <a
                            href={set.affiliateLinks.pga}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-3 py-2 bg-red-500 hover:bg-red-600 text-white text-sm rounded-lg transition-colors font-medium"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            PGA Superstore
                          </a>
                        )}
                        {set.affiliateLinks.rogerDunn && (
                          <a
                            href={set.affiliateLinks.rogerDunn}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center px-3 py-2 bg-purple-500 hover:bg-purple-600 text-white text-sm rounded-lg transition-colors font-medium"
                          >
                            <ExternalLink className="w-3 h-3 mr-1" />
                            Roger Dunn
                          </a>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        💡 Prices may vary. We earn a small commission from purchases at no extra cost to you.
                      </p>
                    </div>
                  )}

                  {/* Action Button */}
                  <div className="space-y-2">
                    <button
                      onClick={() => selectStarterSet(set.id)}
                      className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors ${
                        set.tier === 'good' ? 'bg-green-600 hover:bg-green-700 text-white' :
                        set.tier === 'better' ? 'bg-blue-600 hover:bg-blue-700 text-white' :
                        'bg-purple-600 hover:bg-purple-700 text-white'
                      }`}
                    >
                      📋 Add to My ClubIQ Bag
                    </button>
                    <p className="text-xs text-gray-500 text-center">
                      Save this set to your profile for tracking and analysis
                    </p>
                  </div>
                </div>

              </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
    </>
  );
};

export default BeginnerBuilder;

